import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box,
    Modal,
    Button,
    IconButton,
    Chip,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    Tooltip,
    Tabs,
    Tab,
    Pagination
} from '@mui/material';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import { getData, postData, putData, deleteData } from '../../../webService/webservice.js';
import { useStore } from '../../../Context/context.jsx';
import '../../../styles/Auth/package-management.css';
import TVFMLoader from '../Loader/loader.jsx';
import { generateWalletPrintHTML } from '../../../utils/invoicePrintTemplates.js';

/**
 * Utility to calculate GST Amount and Total Amount in real-time
 */
export const calculateWalletAmountClient = (price, isGstApplicable, gstPercentage, gstType) => {
    const basePrice = Number(price) || 0;
    const gst = Number(gstPercentage) || 0;

    let gstAmount = 0;
    let totalAmount = basePrice;

    if (isGstApplicable && gst > 0) {
        if (gstType === 'Exclusive') {
            gstAmount = (basePrice * gst) / 100;
            totalAmount = basePrice + gstAmount;
        } else if (gstType === 'Inclusive') {
            gstAmount = basePrice - (basePrice / (1 + gst / 100));
            totalAmount = basePrice;
        }
    }

    return {
        gstAmount: Number(gstAmount.toFixed(2)),
        totalAmount: Number(totalAmount.toFixed(2))
    };
};

export const getWalletPurchasedAmount = (w) => {
    if (!w) return 0;
    if (w.amountPaid && Number(w.amountPaid) > 0) return Number(w.amountPaid);
    if (w.price && Number(w.price) > 0) return Number(w.price);
    if (w.walletPackageId?.price && Number(w.walletPackageId.price) > 0) return Number(w.walletPackageId.price);
    if (w.walletPackageId?.totalAmount && Number(w.walletPackageId.totalAmount) > 0) return Number(w.walletPackageId.totalAmount);

    if (w.type === 'Service') {
        if (w.services && w.services.length > 0) {
            const sum = w.services.reduce((acc, s) => {
                const sPrice = Number(s.serviceId?.servicePrice || s.serviceId?.price || s.price || 0);
                const count = Number(s.totalCount || s.baseCount || 1);
                return acc + (sPrice * count);
            }, 0);
            if (sum > 0) return sum;
        }
        if (w.serviceId) {
            const sPrice = Number(w.serviceId?.servicePrice || w.serviceId?.price || w.price || 0);
            const count = Number(w.totalCount || w.baseCount || 1);
            if (sPrice * count > 0) return sPrice * count;
        }
    }

    return Number(w.totalCredited || w.balance || 0);
};

const WalletManagementPage = () => {
    const navigate = useNavigate();
    const { selectedStoreId } = useStore();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const userStoreId = selectedStoreId || localStorage.getItem("storeId") || sessionStorage.getItem("storeId") || sessionStorage.getItem("selectedStoreId");

    const [activeTab, setActiveTab] = useState(0); // 0: Packages, 1: History
    const [loading, setLoading] = useState(false);

    // Customer Wallets state
    const [customerWallets, setCustomerWallets] = useState([]);
    const [packages, setPackages] = useState([]);
    const [services, setServices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');

    // Modals & Details
    const [selectedWalletDetails, setSelectedWalletDetails] = useState(null);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [openSingleHistoryModal, setOpenSingleHistoryModal] = useState(false);
    const [singleCustomerHistory, setSingleCustomerHistory] = useState([]);
    const [singleHistoryLoading, setSingleHistoryLoading] = useState(false);

    // Stats
    const [stats, setStats] = useState({ total: 0, service: 0, cash: 0, activeWallets: 0 });

    // Modals
    const [openModal, setOpenModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedPackageId, setSelectedPackageId] = useState(null);

    // History state
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historySearch, setHistorySearch] = useState('');

    // Pagination State
    const [walletsPage, setWalletsPage] = useState(1);
    const [walletsLimit, setWalletsLimit] = useState(10);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLimit, setHistoryLimit] = useState(10);

    // Date Filter State
    const [datePreset, setDatePreset] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Form State
    const initialFormState = {
        name: '',
        type: 'Service', // 'Service' or 'Cash'
        price: '',
        isGstApplicable: false,
        gstPercentage: '',
        gstType: 'Exclusive',
        validityDays: '180',
        serviceId: '',
        totalServiceCount: '10',
        services: [{ serviceId: '', baseCount: '10', additionalCount: '0' }],
        creditedAmount: '',
        isActive: true
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});

    // Fetch Services for dropdown
    const fetchServices = useCallback(async () => {
        try {
            const token = localStorage.getItem("Token");
            const params = { page: 1, limit: 300 };
            if (userStoreId && userStoreId !== 'null' && userStoreId !== 'undefined' && userStoreId !== 'all') {
                params.storeId = userStoreId;
            }
            const res = await getData("services/services", params, token);
            if (res && res.data) {
                const sData = Array.isArray(res.data) ? res.data[0] : res.data;
                const list = sData?.paginatedResults || res.data.services || (Array.isArray(res.data) ? res.data : []);
                setServices(Array.isArray(list) ? list : []);
            }
        } catch (err) {
            console.error("Error fetching services:", err);
        }
    }, [userStoreId]);

    // Fetch Customer-assigned Wallets
    const fetchCustomerWallets = useCallback(async (sDate, eDate) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            const validStore = (userStoreId && userStoreId !== 'null' && userStoreId !== 'undefined') ? userStoreId : 'all';
            const params = {};
            const activeStart = sDate !== undefined ? sDate : startDate;
            const activeEnd = eDate !== undefined ? eDate : endDate;
            if (activeStart) params.startDate = activeStart;
            if (activeEnd) params.endDate = activeEnd;

            const res = await getData(`wallets/all-customers/${validStore}`, params, token);
            if (res && res.data) {
                const cwList = Array.isArray(res.data) ? res.data : [];
                setCustomerWallets(cwList);
                setStats(prev => ({
                    ...prev,
                    total: cwList.length,
                    service: cwList.filter(w => w.type === 'Service').length,
                    cash: cwList.filter(w => w.type === 'Cash').length
                }));
            }
        } catch (err) {
            console.error("Error fetching customer wallets:", err);
            toast.error("Error loading customer wallets");
        } finally {
            setLoading(false);
        }
    }, [userStoreId, startDate, endDate]);

    // Fetch Wallet History Logs
    const fetchHistoryLogs = useCallback(async (sDate, eDate) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            const validStore = (userStoreId && userStoreId !== 'null' && userStoreId !== 'undefined') ? userStoreId : 'all';
            const params = {};
            const activeStart = sDate !== undefined ? sDate : startDate;
            const activeEnd = eDate !== undefined ? eDate : endDate;
            if (activeStart) params.startDate = activeStart;
            if (activeEnd) params.endDate = activeEnd;

            const res = await getData(`wallets/history/${validStore}`, params, token);
            if (res && res.data) {
                const logs = Array.isArray(res.data) ? res.data : [];
                setHistoryLogs(logs);
                setStats(prev => ({
                    ...prev,
                    activeWallets: logs.length
                }));
            }
        } catch (err) {
            console.error("Error fetching wallet history:", err);
        } finally {
            setLoading(false);
        }
    }, [userStoreId, startDate, endDate]);

    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        const now = new Date();
        let s = "";
        let e = "";

        const formatDateStr = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        if (preset === "today") {
            s = formatDateStr(now);
            e = formatDateStr(now);
        } else if (preset === "yesterday") {
            const y = new Date();
            y.setDate(y.getDate() - 1);
            s = formatDateStr(y);
            e = formatDateStr(y);
        } else if (preset === "this_week") {
            const firstDay = new Date(now);
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1);
            firstDay.setDate(diff);
            s = formatDateStr(firstDay);
            e = formatDateStr(now);
        } else if (preset === "this_month") {
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            s = formatDateStr(firstDay);
            e = formatDateStr(now);
        } else if (preset === "custom") {
            return;
        }

        setStartDate(s);
        setEndDate(e);
        fetchCustomerWallets(s, e);
        fetchHistoryLogs(s, e);
    };

    const walletMetrics = useMemo(() => {
        let cashCount = 0;
        let cashCredited = 0;
        let cashUsed = 0;

        let serviceCount = 0;
        let serviceValue = 0;
        let serviceUsagesTotal = 0;
        let serviceUsagesUsed = 0;

        // 1. Calculate base counts & totals from customerWallets
        (customerWallets || []).forEach(w => {
            if (w.type === 'Cash') {
                cashCount++;
                const credited = Number(w.totalCredited || w.creditedAmount || w.amountPaid || w.balance || 0);
                const bal = Number(w.balance || 0);
                cashCredited += credited;
                cashUsed += Math.max(0, credited - bal);
            } else if (w.type === 'Service') {
                serviceCount++;
                const purchasedAmt = Number(w.amountPaid || w.price || w.walletPackageId?.price || 0);
                serviceValue += purchasedAmt;

                if (Array.isArray(w.services) && w.services.length > 0) {
                    w.services.forEach(s => {
                        const tot = Number(s.totalCount || s.baseCount || 0);
                        const rem = Number(s.remainingCount ?? tot);
                        const used = Number(s.usedCount ?? Math.max(0, tot - rem));
                        serviceUsagesTotal += tot;
                        serviceUsagesUsed += used;
                    });
                } else {
                    const tot = Number(w.totalCount || w.baseCount || 0);
                    const rem = Number(w.remainingCount ?? tot);
                    const used = Number(w.usedCount ?? Math.max(0, tot - rem));
                    serviceUsagesTotal += tot;
                    serviceUsagesUsed += used;
                }
            }
        });

        // 2. Refine usage metrics using transaction history logs when available for date filter ranges
        let historyCashUsed = 0;
        let historyCashCredited = 0;
        let historyServiceUsed = 0;
        let historyServicePurchased = 0;
        let hasCashUsageHistory = false;
        let hasCashCreditHistory = false;
        let hasServiceUsageHistory = false;
        let hasServicePurchaseHistory = false;

        (historyLogs || []).forEach(log => {
            const wType = log.walletType || log.walletId?.type;
            const tType = log.transactionType || '';

            if (wType === 'Cash') {
                const usedAmt = Number(log.walletAmountUsed || log.amountUsed || log.debitAmount || 0);
                if (usedAmt > 0 || tType === 'Service Usage' || tType === 'Cash Usage' || tType === 'Debit') {
                    historyCashUsed += usedAmt;
                    hasCashUsageHistory = true;
                }
                const credAmt = Number(log.walletCredit || log.creditedAmount || log.totalAmount || log.amountPaid || 0);
                if ((tType === 'Purchase' || tType === 'Recharge' || tType === 'Topup') && credAmt > 0) {
                    historyCashCredited += credAmt;
                    hasCashCreditHistory = true;
                }
            } else if (wType === 'Service') {
                const usedCount = Number(log.usedServiceCount || log.serviceCount || log.count || 0);
                if (usedCount > 0 || tType === 'Service Usage' || tType === 'Usage') {
                    historyServiceUsed += (usedCount || 1);
                    hasServiceUsageHistory = true;
                }
                const totalCount = Number(log.totalServiceCount || log.serviceCount || log.baseCount || 0);
                if ((tType === 'Purchase' || tType === 'Recharge') && totalCount > 0) {
                    historyServicePurchased += totalCount;
                    hasServicePurchaseHistory = true;
                }
            }
        });

        const finalCashUsed = (datePreset !== 'all' && hasCashUsageHistory) ? historyCashUsed : cashUsed;
        const finalCashCredited = (datePreset !== 'all' && hasCashCreditHistory && historyCashCredited > 0) ? historyCashCredited : cashCredited;
        const cashRemaining = Math.max(0, finalCashCredited - finalCashUsed);

        const finalServiceUsed = (datePreset !== 'all' && hasServiceUsageHistory) ? historyServiceUsed : serviceUsagesUsed;
        const finalServicePurchased = (datePreset !== 'all' && hasServicePurchaseHistory && historyServicePurchased > 0) ? historyServicePurchased : serviceUsagesTotal;
        const serviceRemaining = Math.max(0, finalServicePurchased - finalServiceUsed);

        return {
            cashCount,
            cashCredited: finalCashCredited,
            cashUsed: finalCashUsed,
            cashRemaining,

            serviceCount,
            serviceUsagesTotal: finalServicePurchased,
            serviceUsagesUsed: finalServiceUsed,
            serviceUsagesRemaining: serviceRemaining
        };
    }, [customerWallets, historyLogs, datePreset]);

    // Fetch History for a Single Customer
    const fetchSingleCustomerHistory = async (wallet) => {
        if (!wallet || !wallet.customerId) return;
        setSingleHistoryLoading(true);
        setSelectedWalletDetails(wallet);
        setOpenSingleHistoryModal(true);

        try {
            const token = localStorage.getItem("Token");
            const custId = wallet.customerId._id || wallet.customerId;
            const res = await getData(`wallets/customer-history/${custId}`, {}, token);
            if (res && res.data) {
                setSingleCustomerHistory(Array.isArray(res.data) ? res.data : []);
            }
        } catch (err) {
            console.error("Error fetching single customer wallet history:", err);
        } finally {
            setSingleHistoryLoading(false);
        }
    };

    // Thermal Print Wallet Receipt
    const handlePrintWallet = (walletOrLog) => {
        try {
            const printHTML = generateWalletPrintHTML(walletOrLog);
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            document.body.appendChild(iframe);

            const doc = iframe.contentWindow.document;
            doc.open();
            doc.write(printHTML);
            doc.close();

            iframe.contentWindow.focus();
            setTimeout(() => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            }, 500);
        } catch (err) {
            console.error("Print Wallet Error:", err);
            toast.error("Failed to print wallet details");
        }
    };

    useEffect(() => {
        fetchServices();
        fetchCustomerWallets();
        fetchHistoryLogs();
    }, [fetchServices, fetchCustomerWallets, fetchHistoryLogs]);

    useEffect(() => {
        if (openModal) {
            fetchServices();
        }
    }, [openModal, fetchServices]);

    // Real-time calculation summary
    const calculatedAmount = calculateWalletAmountClient(
        formData.price,
        formData.isGstApplicable,
        formData.gstPercentage,
        formData.gstType
    );

    // Form inputs handler
    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'isGstApplicable' && !value) {
                updated.gstPercentage = '';
            }
            return updated;
        });

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleAddServiceRow = () => {
        setFormData(prev => ({
            ...prev,
            services: [...(prev.services || []), { serviceId: '', baseCount: '10', additionalCount: '0' }]
        }));
    };

    const handleRemoveServiceRow = (index) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.filter((_, i) => i !== index)
        }));
    };

    const handleServiceRowChange = (index, field, value) => {
        setFormData(prev => {
            const updated = [...(prev.services || [])];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, services: updated };
        });
        if (errors.services) {
            setErrors(prev => ({ ...prev, services: null }));
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Wallet name is required";
        if (!formData.price || Number(formData.price) <= 0) newErrors.price = "Valid price is required";
        if (!formData.validityDays || Number(formData.validityDays) <= 0) newErrors.validityDays = "Validity in days is required";

        if (formData.type === 'Service') {
            const hasValidServices = Array.isArray(formData.services) && formData.services.length > 0 && formData.services.every(s => s.serviceId && (Number(s.baseCount || s.allowedUsages || 0) + Number(s.additionalCount || 0)) > 0);
            if (!hasValidServices) {
                newErrors.services = "Please select at least one service with a valid usage count";
            }
        } else if (formData.type === 'Cash') {
            if (!formData.creditedAmount || Number(formData.creditedAmount) <= 0) {
                newErrors.creditedAmount = "Credited wallet amount is required";
            }
        }

        if (formData.isGstApplicable) {
            if (!formData.gstPercentage || Number(formData.gstPercentage) <= 0) {
                newErrors.gstPercentage = "GST percentage is required";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Open Add Modal
    const handleOpenAddModal = () => {
        setIsEditMode(false);
        setSelectedPackageId(null);
        setFormData(initialFormState);
        setErrors({});
        setOpenModal(true);
    };

    // Open Edit Modal
    const handleOpenEditModal = (pkg) => {
        setIsEditMode(true);
        setSelectedPackageId(pkg._id);

        let editServices = [];
        if (pkg.services && pkg.services.length > 0) {
            editServices = pkg.services.map(s => ({
                serviceId: s.serviceId?._id || s.serviceId || '',
                baseCount: String(s.baseCount || s.totalServiceCount || s.allowedUsages || 10),
                additionalCount: String(s.additionalCount || 0)
            }));
        } else if (pkg.serviceId) {
            editServices = [{
                serviceId: pkg.serviceId?._id || pkg.serviceId || '',
                baseCount: String(pkg.totalServiceCount || 10),
                additionalCount: '0'
            }];
        } else {
            editServices = [{ serviceId: '', baseCount: '10', additionalCount: '0' }];
        }

        setFormData({
            name: pkg.name || '',
            type: pkg.type || 'Service',
            price: pkg.price || '',
            isGstApplicable: pkg.isGstApplicable || false,
            gstPercentage: pkg.gstPercentage || '',
            gstType: pkg.gstType || 'Exclusive',
            validityDays: pkg.validityDays || '180',
            serviceId: pkg.serviceId?._id || pkg.serviceId || '',
            totalServiceCount: pkg.totalServiceCount || '',
            services: editServices,
            creditedAmount: pkg.creditedAmount || '',
            isActive: pkg.isActive !== false
        });
        setErrors({});
        setOpenModal(true);
    };

    // Submit Form (Create / Edit)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const formattedServices = formData.type === 'Service' && Array.isArray(formData.services)
                ? formData.services.map(s => {
                    const bCount = Number(s.baseCount || s.allowedUsages || 1);
                    const aCount = Number(s.additionalCount || 0);
                    return {
                        serviceId: s.serviceId,
                        baseCount: bCount,
                        additionalCount: aCount,
                        totalServiceCount: bCount + aCount
                    };
                })
                : undefined;

            const primaryServiceId = formattedServices && formattedServices.length > 0 ? formattedServices[0].serviceId : formData.serviceId;
            const totalServiceCountSum = formattedServices && formattedServices.length > 0
                ? formattedServices.reduce((sum, s) => sum + s.totalServiceCount, 0)
                : Number(formData.totalServiceCount || 0);

            const payload = {
                storeId: userStoreId,
                name: formData.name,
                type: formData.type,
                price: Number(formData.price),
                isGstApplicable: formData.isGstApplicable,
                gstPercentage: formData.isGstApplicable ? Number(formData.gstPercentage) : 0,
                gstType: formData.gstType,
                gstAmount: calculatedAmount.gstAmount,
                totalAmount: calculatedAmount.totalAmount,
                validityDays: Number(formData.validityDays),
                serviceId: formData.type === 'Service' ? primaryServiceId : undefined,
                totalServiceCount: formData.type === 'Service' ? totalServiceCountSum : undefined,
                services: formattedServices,
                creditedAmount: formData.type === 'Cash' ? Number(formData.creditedAmount) : undefined,
                isActive: formData.isActive
            };

            const token = localStorage.getItem("Token") || sessionStorage.getItem("Token");
            let res;
            if (isEditMode && selectedPackageId) {
                res = await putData(`wallets/package/${selectedPackageId}`, payload, token);
            } else {
                res = await postData("wallets/create-package", payload, token);
            }

            if (res && (res.status === 200 || res.status === 201 || res.data)) {
                toast.success(`Wallet Package ${isEditMode ? 'updated' : 'created'} successfully!`);
                setOpenModal(false);
                fetchCustomerWallets();
            } else {
                toast.error(res?.message || `Failed to ${isEditMode ? 'update' : 'create'} wallet package`);
            }
        } catch (err) {
            console.error("Error saving wallet package:", err);
            toast.error("An error occurred while saving wallet package");
        } finally {
            setLoading(false);
        }
    };

    // Delete / Deactivate Package Plan
    const handleDeletePackage = async (pkgId, pkgName) => {
        Swal.fire({
            title: 'Deactivate Wallet Plan?',
            text: `Are you sure you want to deactivate "${pkgName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Deactivate'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const token = localStorage.getItem("Token") || sessionStorage.getItem("Token");
                    const res = await deleteData(`wallets/package/${pkgId}`, token);
                    if (res && (res.status === 200 || res.message)) {
                        toast.success("Wallet package deactivated successfully");
                        fetchCustomerWallets();
                    } else {
                        toast.error(res?.message || "Failed to deactivate package");
                    }
                } catch (err) {
                    console.error("Error deactivating package:", err);
                    toast.error("Error deactivating package");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Filter Packages
    const filteredPackages = packages.filter(pkg => {
        const matchesSearch = pkg.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            pkg.serviceId?.serviceName?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = typeFilter === 'All' || pkg.type === typeFilter;
        return matchesSearch && matchesType;
    });

    // Filter Customer Wallets
    const filteredCustomerWallets = (customerWallets || []).filter(w => {
        const custName = w.customerId?.name || w.customerId?.customerName || '';
        const phone = w.customerId?.phoneNumber || '';
        const planName = w.customWalletName || w.walletPackageId?.name || (w.type === 'Service' ? `${w.serviceId?.serviceName || 'Service'} Package` : 'Price Wallet');
        const serviceName = w.serviceId?.serviceName || '';
        const query = searchQuery.toLowerCase();

        const matchesSearch = custName.toLowerCase().includes(query) ||
            phone.toLowerCase().includes(query) ||
            planName.toLowerCase().includes(query) ||
            serviceName.toLowerCase().includes(query);

        const matchesType = typeFilter === 'All' || w.type === typeFilter;

        const isExpired = new Date() > new Date(w.expiryDate) || w.status === 'EXPIRED';
        const currentStatus = isExpired ? 'Expired' : (w.status || 'Active');

        const matchesStatus = statusFilter === 'All' ||
            (statusFilter === 'Active' && (w.status === 'ACTIVE' || w.status === 'Active') && !isExpired) ||
            (statusFilter === 'Expired' && (currentStatus === 'EXPIRED' || isExpired));

        return matchesSearch && matchesType && matchesStatus;
    });

    // Filter History Logs
    const filteredHistory = historyLogs.filter(log => {
        const custName = log.customerId?.name || log.customerId?.customerName || '';
        const phone = log.customerId?.phoneNumber || '';
        const walletName = log.walletName || '';
        const billNum = log.posBillNumber || '';
        const query = historySearch.toLowerCase();

        return custName.toLowerCase().includes(query) ||
            phone.toLowerCase().includes(query) ||
            walletName.toLowerCase().includes(query) ||
            billNum.toLowerCase().includes(query);
    });

    // Reset pagination on filter changes
    useEffect(() => {
        setWalletsPage(1);
    }, [searchQuery, typeFilter, statusFilter]);

    useEffect(() => {
        setHistoryPage(1);
    }, [historySearch]);

    // Paginated Customer Wallets (Tab 0)
    const totalWalletsCount = filteredCustomerWallets.length;
    const totalWalletsPages = walletsLimit === 'all' ? 1 : (Math.ceil(totalWalletsCount / walletsLimit) || 1);
    const paginatedCustomerWallets = walletsLimit === 'all'
        ? filteredCustomerWallets
        : filteredCustomerWallets.slice((walletsPage - 1) * walletsLimit, walletsPage * walletsLimit);

    // Paginated History Logs (Tab 1)
    const totalHistoryCount = filteredHistory.length;
    const totalHistoryPages = historyLimit === 'all' ? 1 : (Math.ceil(totalHistoryCount / historyLimit) || 1);
    const paginatedHistory = historyLimit === 'all'
        ? filteredHistory
        : filteredHistory.slice((historyPage - 1) * historyLimit, historyPage * historyLimit);

    return (
        <div className='product-layout'>
            <div className="package-management-container">
                {loading && <TVFMLoader />}

                {/* Header */}
                <div className="package-header">
                    <div className="package-header-title">
                        <div className="package-header-icon">
                            <Icon icon="solar:wallet-money-bold-duotone" width="26" height="26" />
                        </div>
                        <div>
                            <h2>Wallet Reports</h2>
                            <p>Create & manage Service Wallets, Cash Wallets, and track customer usage history</p>
                        </div>
                    </div>
                </div>

                {/* Date Range Filter Bar */}
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon icon="solar:calendar-bold-duotone" width="20" height="20" style={{ color: '#3C0008' }} />
                        <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Date Filter:</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {['all', 'today', 'yesterday', 'this_week', 'this_month', 'custom'].map((preset) => {
                            const labels = {
                                all: 'All Time',
                                today: 'Today',
                                yesterday: 'Yesterday',
                                this_week: 'This Week',
                                this_month: 'This Month',
                                custom: 'Custom Range'
                            };
                            const isActive = datePreset === preset;
                            return (
                                <button
                                    key={preset}
                                    type="button"
                                    onClick={() => handlePresetChange(preset)}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        border: `1px solid ${isActive ? '#3C0008' : '#cbd5e1'}`,
                                        background: isActive ? '#3C0008' : '#f8fafc',
                                        color: isActive ? '#ffffff' : '#475569',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {labels[preset]}
                                </button>
                            );
                        })}

                        {datePreset === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        fetchCustomerWallets(e.target.value, endDate);
                                        fetchHistoryLogs(e.target.value, endDate);
                                    }}
                                    style={{
                                        padding: '5px 9px',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '12px',
                                        color: '#0f172a'
                                    }}
                                />
                                <span style={{ fontSize: '12px', color: '#64748b' }}>to</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        fetchCustomerWallets(startDate, e.target.value);
                                        fetchHistoryLogs(startDate, e.target.value);
                                    }}
                                    style={{
                                        padding: '5px 9px',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '12px',
                                        color: '#0f172a'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Cash Wallets & Service Wallets Breakdown Section */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '20px',
                    marginBottom: '24px'
                }}>
                    {/* CASH WALLETS SUMMARY CARD */}
                    <div style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        padding: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        borderTop: '4px solid #10b981'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px dashed #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    background: '#d1fae5',
                                    color: '#059669',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Icon icon="solar:wallet-money-bold-duotone" width="22" height="22" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a', letterSpacing: '0.3px' }}>CASH WALLETS</h3>
                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Monetary Credit Summary</span>
                                </div>
                            </div>
                            <Chip label={`${walletMetrics.cashCount} Wallets`} size="small" style={{ background: '#ecfdf5', color: '#047857', fontWeight: 700, fontSize: '12px' }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Cash Wallet Count</span>
                                <strong style={{ color: '#0f172a', fontSize: '14px' }}>{walletMetrics.cashCount}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Amount Credited</span>
                                <strong style={{ color: '#059669', fontSize: '14px' }}>{walletMetrics.cashCredited.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Amount Used</span>
                                <strong style={{ color: '#dc2626', fontSize: '14px' }}>{walletMetrics.cashUsed.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '13.5px',
                                paddingTop: '10px',
                                borderTop: '1px solid #f1f5f9',
                                marginTop: '4px'
                            }}>
                                <span style={{ color: '#0f172a', fontWeight: 700 }}>Total Remaining Balance</span>
                                <strong style={{ color: '#0284c7', fontSize: '15px' }}>{walletMetrics.cashRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </div>
                        </div>
                    </div>

                    {/* SERVICE WALLETS SUMMARY CARD */}
                    <div style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        padding: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        borderTop: '4px solid #8b5cf6'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px dashed #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    background: '#f3e8ff',
                                    color: '#7c3aed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Icon icon="solar:shop-2-bold-duotone" width="22" height="22" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a', letterSpacing: '0.3px' }}>SERVICE WALLETS</h3>
                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Usage Credits Summary</span>
                                </div>
                            </div>
                            <Chip label={`${walletMetrics.serviceCount} Wallets`} size="small" style={{ background: '#faf5ff', color: '#6b21a8', fontWeight: 700, fontSize: '12px' }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Service Wallet Count</span>
                                <strong style={{ color: '#0f172a', fontSize: '14px' }}>{walletMetrics.serviceCount}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Services/Credits Purchased</span>
                                <strong style={{ color: '#7c3aed', fontSize: '14px' }}>{walletMetrics.serviceUsagesTotal} Uses</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>Total Services Used</span>
                                <strong style={{ color: '#d97706', fontSize: '14px' }}>{walletMetrics.serviceUsagesUsed} Uses</strong>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '13.5px',
                                paddingTop: '10px',
                                borderTop: '1px solid #f1f5f9',
                                marginTop: '4px'
                            }}>
                                <span style={{ color: '#0f172a', fontWeight: 700 }}>Total Services Remaining</span>
                                <strong style={{ color: '#0284c7', fontSize: '15px' }}>{walletMetrics.serviceUsagesRemaining} Uses</strong>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, val) => setActiveTab(val)}
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '14px',
                                minHeight: '44px'
                            },
                            '& .Mui-selected': {
                                color: '#3C0008 !important'
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#3C0008'
                            }
                        }}
                    >
                        <Tab label="Customer Wallets" icon={<Icon icon="mdi:account-badge" width="18" />} iconPosition="start" />
                        <Tab label="Customer Wallet Transactions" icon={<Icon icon="mdi:history" width="18" />} iconPosition="start" />
                    </Tabs>
                </Box>

                {/* TAB 0: CUSTOMER WALLETS */}
                {activeTab === 0 && (
                    <>
                        <div className="package-controls-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                            <div className="search-box-wrapper" style={{ flex: 1, minWidth: '260px', maxWidth: '450px' }}>
                                <Icon icon="mdi:magnify" width="20" height="20" style={{ color: '#64748b' }} />
                                <input
                                    type="text"
                                    placeholder="Search by customer name, mobile number, or wallet plan..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <Icon icon="mdi:close" width="16" height="16" />
                                    </IconButton>
                                )}
                            </div>

                            <div className="filter-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: '12px' }}>
                                <TextField
                                    select
                                    size="small"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    sx={{
                                        minWidth: 140,
                                        background: '#f8fafc',
                                        borderRadius: '10px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px'
                                        }
                                    }}
                                >
                                    <MenuItem value="All">All Types</MenuItem>
                                    <MenuItem value="Service">Service Wallet</MenuItem>
                                    <MenuItem value="Cash">Cash Wallet</MenuItem>
                                </TextField>

                                <TextField
                                    select
                                    size="small"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    sx={{
                                        minWidth: 130,
                                        background: '#f8fafc',
                                        borderRadius: '10px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px'
                                        }
                                    }}
                                >
                                    <MenuItem value="All">All Status</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Expired">Expired</MenuItem>
                                </TextField>
                            </div>
                        </div>

                        {/* Customer Wallets Table */}
                        <Paper className="package-table-container">
                            <TableContainer>
                                <Table className="package-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Mobile Number</TableCell>
                                            <TableCell>Wallet Type</TableCell>
                                            <TableCell>Wallet Plan</TableCell>
                                            <TableCell>Service / Credit</TableCell>
                                            <TableCell>Purchased Amount</TableCell>
                                            <TableCell>Wallet Balance / Remaining</TableCell>
                                            <TableCell>Validity</TableCell>
                                            <TableCell>Expiry Date</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredCustomerWallets.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                    <Typography color="textSecondary">No customer wallets found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedCustomerWallets.map((w) => {
                                                const custName = w.customerId?.name || w.customerId?.customerName || 'Customer';
                                                const phone = w.customerId?.phoneNumber || '-';
                                                const planName = w.customWalletName || w.walletPackageId?.name || (w.type === 'Service' ? `${w.serviceId?.serviceName || 'Service'} Package` : 'Price Wallet');
                                                const isExpired = new Date() > new Date(w.expiryDate) || w.status === 'EXPIRED';
                                                const expiryStr = w.expiryDate ? new Date(w.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
                                                const purchasedPrice = getWalletPurchasedAmount(w);

                                                return (
                                                    <TableRow key={w._id} hover>
                                                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{custName}</TableCell>
                                                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>{phone}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={w.type === 'Service' ? 'Service Wallet' : 'Cash Wallet'}
                                                                color={w.type === 'Service' ? 'primary' : 'secondary'}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 700, borderRadius: '6px' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{planName}</TableCell>
                                                        <TableCell>
                                                            {w.type === 'Service' ? (
                                                                <div>
                                                                    {w.services && w.services.length > 0 ? (
                                                                        w.services.map((s, sIdx) => (
                                                                            <div key={s._id || s.serviceId?._id || sIdx} style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '2px' }}>
                                                                                • {s.serviceId?.serviceName || 'Service'}: {s.totalCount || s.baseCount || 0} Uses
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <>
                                                                            <div style={{ fontWeight: 600, color: '#1e40af' }}>{w.serviceId?.serviceName || w.services?.map(s => s.serviceId?.serviceName || s.serviceName).filter(Boolean).join(', ') || w.customWalletName || w.walletPackageId?.name || 'Service'}</div>
                                                                            <div style={{ fontSize: '12px', color: '#64748b' }}>Usages: {w.totalCount || 0} Uses</div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <div style={{ fontWeight: 600, color: '#16a34a' }}>Credited: ₹{w.totalCredited || w.balance || 0}</div>
                                                                    <div style={{ fontSize: '12px', color: '#64748b' }}>Pays: ₹{purchasedPrice}</div>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>₹{purchasedPrice}</TableCell>
                                                        <TableCell>
                                                            {w.type === 'Service' ? (
                                                                w.services && w.services.length > 0 ? (
                                                                    <div style={{ fontSize: '12px' }}>
                                                                        {w.services.map((s, sIdx) => (
                                                                            <div key={s._id || s.serviceId?._id || sIdx} style={{ fontWeight: 700, color: (s.remainingCount || 0) > 0 ? '#16a34a' : '#ef4444', marginBottom: '2px' }}>
                                                                                {s.serviceId?.serviceName || 'Service'}: {s.remainingCount || 0} / {s.totalCount || 0} Left
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '13px' }}>
                                                                        {w.remainingCount} / {w.totalCount} Remaining
                                                                    </div>
                                                                )
                                                            ) : (
                                                                <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '14px' }}>
                                                                    ₹{Number(w.balance || 0).toFixed(2)}
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{w.walletPackageId?.validityDays || 365} Days</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#475569' }}>{expiryStr}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={isExpired ? 'Expired' : 'Active'}
                                                                color={isExpired ? 'error' : 'success'}
                                                                size="small"
                                                                sx={{ fontWeight: 700 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <div className="action-btn-group" style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                                <Tooltip title="View Details">
                                                                    <IconButton size="small" onClick={() => {
                                                                        setSelectedWalletDetails(w);
                                                                        setOpenDetailsModal(true);
                                                                    }} sx={{ color: '#2563eb', background: '#eff6ff', '&:hover': { background: '#dbeafe' } }}>
                                                                        <Icon icon="mdi:eye" width="18" height="18" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {/* <Tooltip title="Print Wallet Details">
                                                                    <IconButton size="small" onClick={() => handlePrintWallet(w)} sx={{ color: '#0284c7', background: '#e0f2fe', '&:hover': { background: '#bae6fd' } }}>
                                                                        <Icon icon="mdi:printer" width="18" height="18" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Wallet History">
                                                                    <IconButton size="small" onClick={() => fetchSingleCustomerHistory(w)} sx={{ color: '#3C0008', background: '#f9f1f2', '&:hover': { background: '#f2e1e4' } }}>
                                                                        <Icon icon="mdi:history" width="18" height="18" />
                                                                    </IconButton>
                                                                </Tooltip> */}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Tab 0 Pagination */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Show</span>
                                    <select
                                        value={walletsLimit}
                                        onChange={(e) => {
                                            const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                                            setWalletsLimit(val);
                                            setWalletsPage(1);
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', color: '#0f172a', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value="all">All</option>
                                    </select>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Entries</span>
                                </div>

                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                    {walletsLimit === 'all'
                                        ? `Showing all ${totalWalletsCount} customer wallets`
                                        : `Showing ${totalWalletsCount > 0 ? Math.min((walletsPage - 1) * walletsLimit + 1, totalWalletsCount) : 0} to ${Math.min(walletsPage * walletsLimit, totalWalletsCount)} of ${totalWalletsCount} customer wallets`
                                    }
                                </Typography>

                                {totalWalletsPages > 1 && (
                                    <Pagination
                                        count={totalWalletsPages}
                                        page={walletsPage}
                                        onChange={(e, page) => setWalletsPage(page)}
                                        color="primary"
                                        size="medium"
                                        showFirstButton
                                        showLastButton
                                        sx={{
                                            '& .MuiPaginationItem-root': { fontWeight: 600, borderRadius: '8px' },
                                            '& .Mui-selected': { bgcolor: '#3C0008 !important', color: '#ffffff' }
                                        }}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </>
                )}

                {/* TAB 1: CUSTOMER WALLET TRANSACTIONS */}
                {activeTab === 1 && (
                    <>
                        <div className="package-controls-card">
                            <div className="search-box-wrapper">
                                <Icon icon="mdi:magnify" width="20" height="20" style={{ color: '#64748b' }} />
                                <input
                                    type="text"
                                    placeholder="Search history by customer name, phone, wallet name, bill #..."
                                    value={historySearch}
                                    onChange={(e) => setHistorySearch(e.target.value)}
                                />
                                {historySearch && (
                                    <IconButton size="small" onClick={() => setHistorySearch('')}>
                                        <Icon icon="mdi:close" width="16" height="16" />
                                    </IconButton>
                                )}
                            </div>
                        </div>

                        <Paper className="package-table-container">
                            <TableContainer>
                                <Table className="package-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Wallet Name & Type</TableCell>
                                            <TableCell>Transaction Type</TableCell>
                                            <TableCell>Paid / Credited</TableCell>
                                            <TableCell>Usage / Balance</TableCell>
                                            <TableCell>Bill # / Method</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredHistory.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                    <Typography color="textSecondary">No wallet transactions found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedHistory.map((log) => (
                                                <TableRow key={log._id} hover>
                                                    <TableCell>{new Date(log.transactionDate || log.createdAt).toLocaleDateString('en-GB')}</TableCell>
                                                    <TableCell>
                                                        <div><strong style={{ color: '#0f172a' }}>{log.customerId?.name || log.customerId?.customerName || 'Customer'}</strong></div>
                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{log.customerId?.phoneNumber || '-'}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div style={{ fontWeight: 600 }}>{log.walletName}</div>
                                                        <Chip label={log.walletType} size="small" variant="outlined" sx={{ fontSize: '10px', height: '18px', mt: '2px' }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={log.transactionType}
                                                            color={
                                                                log.transactionType === 'Purchase' ? 'success' :
                                                                    log.transactionType === 'Recharge' ? 'info' :
                                                                        log.transactionType === 'Service Usage' ? 'warning' : 'secondary'
                                                            }
                                                            size="small"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.transactionType === 'Purchase' || log.transactionType === 'Recharge' ? (() => {
                                                            const totPaid = Number(log.totalAmount || log.amountPaid || log.walletId?.totalAmount || log.walletId?.amountPaid || 0);
                                                            const isGst = Boolean(log.isGstApplicable || log.walletId?.isGstApplicable);
                                                            const gstRate = Number(log.gstPercentage ?? log.walletId?.gstPercentage ?? 0);
                                                            const gstType = log.gstType || log.walletId?.gstType || 'Exclusive';

                                                            let gstAmt = Number(log.gstAmount ?? log.walletId?.gstAmount ?? 0);
                                                            let basePay = Number(log.customerBasePay ?? log.walletId?.customerBasePay ?? 0);

                                                            if (isGst && gstRate > 0) {
                                                                if (gstType === 'Exclusive') {
                                                                    if (!basePay && gstAmt) basePay = totPaid - gstAmt;
                                                                    if (!basePay) basePay = totPaid / (1 + gstRate / 100);
                                                                    if (!gstAmt) gstAmt = totPaid - basePay;
                                                                } else { // Inclusive
                                                                    if (!gstAmt) gstAmt = totPaid - (totPaid / (1 + gstRate / 100));
                                                                    if (!basePay) basePay = totPaid - gstAmt;
                                                                }
                                                            } else {
                                                                basePay = totPaid;
                                                                gstAmt = 0;
                                                            }

                                                            return (
                                                                <div>
                                                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>Paid: ₹{totPaid.toFixed(2)}</div>
                                                                    {isGst && gstRate > 0 ? (
                                                                        <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px', lineHeight: '1.3' }}>
                                                                            <div>Base: <strong>₹{basePay.toFixed(2)}</strong></div>
                                                                            <div>GST ({gstType} {gstRate}%): <strong>₹{gstAmt.toFixed(2)}</strong></div>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>GST: N/A</div>
                                                                    )}
                                                                    {log.walletCredit > 0 && (
                                                                        <div style={{ color: '#16a34a', fontSize: '11.5px', fontWeight: 600, marginTop: '3px' }}>
                                                                            Credit: ₹{log.walletCredit}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })() : (
                                                            <span>-</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.walletType === 'Service' ? (
                                                            <div>Rem: <strong>{log.remainingServiceCount}</strong> Usages</div>
                                                        ) : (
                                                            <div>
                                                                {log.walletAmountUsed > 0 && <div style={{ color: '#dc2626', fontWeight: 600 }}>Used: ₹{log.walletAmountUsed}</div>}
                                                                <div>Bal: <strong>₹{log.remainingWalletBalance}</strong></div>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div style={{ fontWeight: 600 }}>{log.posBillNumber || '-'}</div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>{log.paymentMethod || '-'}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={log.status || 'SUCCESS'} color="success" size="small" sx={{ fontWeight: 600 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Print Wallet Receipt">
                                                            <IconButton size="small" onClick={() => handlePrintWallet(log)} sx={{ color: '#0284c7', background: '#e0f2fe', '&:hover': { background: '#bae6fd' } }}>
                                                                <Icon icon="mdi:printer" width="18" height="18" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Tab 1 Pagination */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Show</span>
                                    <select
                                        value={historyLimit}
                                        onChange={(e) => {
                                            const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                                            setHistoryLimit(val);
                                            setHistoryPage(1);
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', color: '#0f172a', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value="all">All</option>
                                    </select>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Entries</span>
                                </div>

                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                    {historyLimit === 'all'
                                        ? `Showing all ${totalHistoryCount} transactions`
                                        : `Showing ${totalHistoryCount > 0 ? Math.min((historyPage - 1) * historyLimit + 1, totalHistoryCount) : 0} to ${Math.min(historyPage * historyLimit, totalHistoryCount)} of ${totalHistoryCount} transactions`
                                    }
                                </Typography>

                                {totalHistoryPages > 1 && (
                                    <Pagination
                                        count={totalHistoryPages}
                                        page={historyPage}
                                        onChange={(e, page) => setHistoryPage(page)}
                                        color="primary"
                                        size="medium"
                                        showFirstButton
                                        showLastButton
                                        sx={{
                                            '& .MuiPaginationItem-root': { fontWeight: 600, borderRadius: '8px' },
                                            '& .Mui-selected': { bgcolor: '#3C0008 !important', color: '#ffffff' }
                                        }}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </>
                )}

                {/* CREATE / EDIT WALLET PACKAGE MODAL */}
                <Modal open={openModal} onClose={() => setOpenModal(false)}>
                    <Box className="package-modal-box">
                        <div className="modal-header-banner">
                            <h3>
                                <Icon icon="solar:wallet-money-bold-duotone" width="22" height="22" />
                                {isEditMode ? 'Edit Wallet Package Plan' : 'Create New Wallet Package Plan'}
                            </h3>
                            <IconButton size="small" onClick={() => setOpenModal(false)} sx={{ color: '#ffffff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: '24px' }}>

                            {/* SELECT WALLET TYPE SEGMENT CARDS */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '10px' }}>
                                    Select Wallet Type <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="wallet-type-selector">
                                    <div
                                        className={`wallet-type-card ${formData.type === 'Service' ? 'active' : ''}`}
                                        onClick={() => handleInputChange('type', 'Service')}
                                    >
                                        <div className="icon-box">
                                            <Icon icon="solar:shop-2-bold-duotone" width="20" height="20" />
                                        </div>
                                        <div>
                                            <div className="title">Service Wallet</div>
                                            <div className="desc">Fixed usages for specific eligible services</div>
                                        </div>
                                    </div>

                                    <div
                                        className={`wallet-type-card ${formData.type === 'Cash' ? 'active' : ''}`}
                                        onClick={() => handleInputChange('type', 'Cash')}
                                    >
                                        <div className="icon-box">
                                            <Icon icon="solar:wallet-money-bold-duotone" width="20" height="20" />
                                        </div>
                                        <div>
                                            <div className="title">Cash / Price Wallet</div>
                                            <div className="desc">Credited monetary balance for services</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* WALLET NAME FIELD */}
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                    Wallet Package Name <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <TextField
                                    fullWidth
                                    placeholder={formData.type === 'Service' ? 'e.g. Hair Cut Package' : 'e.g. Premium Cash Wallet'}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </div>

                            {/* Service Wallet Specific Fields */}
                            {formData.type === 'Service' && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <label style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                                            Included Services & Usages <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <Button
                                            size="small"
                                            startIcon={<Icon icon="mdi:plus" />}
                                            onClick={handleAddServiceRow}
                                            sx={{ textTransform: 'none', fontWeight: 600, color: '#3C0008' }}
                                        >
                                            Add Service
                                        </Button>
                                    </div>

                                    {errors.services && (
                                        <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>
                                            {errors.services}
                                        </div>
                                    )}

                                    {(formData.services || []).map((row, idx) => (
                                        <div key={idx} style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Service #{idx + 1}</span>
                                                {(formData.services || []).length > 1 && (
                                                    <IconButton size="small" onClick={() => handleRemoveServiceRow(idx)} sx={{ color: '#ef4444' }}>
                                                        <Icon icon="mdi:trash-can-outline" width="18" />
                                                    </IconButton>
                                                )}
                                            </div>

                                            <div style={{ marginBottom: '10px' }}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label="Select Service"
                                                    value={row.serviceId}
                                                    onChange={(e) => handleServiceRowChange(idx, 'serviceId', e.target.value)}
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', background: '#fff' } }}
                                                >
                                                    <MenuItem value="" disabled>-- Select Service --</MenuItem>
                                                    {services.map(s => (
                                                        <MenuItem key={s._id} value={s._id}>
                                                            {s.serviceName} {(s.price || s.nonMemberShipPrice) ? `(₹${s.price || s.nonMemberShipPrice})` : ''}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    label="Base Usage"
                                                    placeholder="10"
                                                    value={row.baseCount}
                                                    onChange={(e) => handleServiceRowChange(idx, 'baseCount', e.target.value)}
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', background: '#fff' } }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    label="Additional Usage"
                                                    placeholder="0"
                                                    value={row.additionalCount}
                                                    onChange={(e) => handleServiceRowChange(idx, 'additionalCount', e.target.value)}
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', background: '#fff' } }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    type="text"
                                                    label="Total Available"
                                                    value={`${(Number(row.baseCount || 0) + Number(row.additionalCount || 0))} Usages`}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', background: '#e2e8f0', fontWeight: 700, color: '#16a34a' } }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Cash Wallet Specific Fields */}
                            {formData.type === 'Cash' && (
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                        Wallet Credit Amount (₹) <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder="e.g. 4500 (Amount credited to customer wallet balance)"
                                        value={formData.creditedAmount}
                                        onChange={(e) => handleInputChange('creditedAmount', e.target.value)}
                                        error={!!errors.creditedAmount}
                                        helperText={errors.creditedAmount || "Balance amount credited to customer upon purchase"}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                    />
                                </div>
                            )}

                            {/* Selling Price & Validity Period */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                        {formData.type === 'Cash' ? "Customer Purchase Amount (₹)" : "Selling Price (₹)"} <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder="e.g. 3000"
                                        value={formData.price}
                                        onChange={(e) => handleInputChange('price', e.target.value)}
                                        error={!!errors.price}
                                        helperText={errors.price}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                        Validity Period (in Days) <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder="e.g. 180 (180 Days = 6 Months)"
                                        value={formData.validityDays}
                                        onChange={(e) => handleInputChange('validityDays', e.target.value)}
                                        error={!!errors.validityDays}
                                        helperText={errors.validityDays}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                    />
                                </div>
                            </div>

                            {/* GST Toggle Pills */}
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '8px' }}>
                                    GST Settings
                                </label>
                                <div className="gst-toggle-pills">
                                    <button
                                        type="button"
                                        className={`gst-pill ${!formData.isGstApplicable ? 'active' : ''}`}
                                        onClick={() => handleInputChange('isGstApplicable', false)}
                                    >
                                        No GST
                                    </button>
                                    <button
                                        type="button"
                                        className={`gst-pill ${formData.isGstApplicable ? 'active' : ''}`}
                                        onClick={() => handleInputChange('isGstApplicable', true)}
                                    >
                                        Apply GST
                                    </button>
                                </div>
                            </div>

                            {formData.isGstApplicable && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                            GST Percentage (%) <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            placeholder="18"
                                            value={formData.gstPercentage}
                                            onChange={(e) => handleInputChange('gstPercentage', e.target.value)}
                                            error={!!errors.gstPercentage}
                                            helperText={errors.gstPercentage}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '12.5px', color: '#334155', marginBottom: '6px' }}>
                                            GST Type
                                        </label>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={formData.gstType}
                                            onChange={(e) => handleInputChange('gstType', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                        >
                                            <MenuItem value="Exclusive">Exclusive (Base Price + GST)</MenuItem>
                                            <MenuItem value="Inclusive">Inclusive (GST included in Base Price)</MenuItem>
                                        </TextField>
                                    </div>
                                </div>
                            )}

                            {/* Realtime Payable Summary Card */}
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569' }}>
                                    <span>Base Amount:</span>
                                    <span style={{ fontWeight: 600 }}>₹{formData.price || 0}</span>
                                </div>
                                {formData.isGstApplicable && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginTop: '6px' }}>
                                        <span>GST ({formData.gstPercentage || 0}% {formData.gstType}):</span>
                                        <span style={{ fontWeight: 600 }}>₹{calculatedAmount.gstAmount}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 700, color: '#0f172a', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                    <span>Total Payable Amount:</span>
                                    <span style={{ color: '#16a34a' }}>₹{calculatedAmount.totalAmount}</span>
                                </div>
                            </div>

                            <div style={{ padding: '20px 0 0 0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <Button onClick={() => setOpenModal(false)} variant="outlined" color="inherit" sx={{ borderRadius: '10px' }}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" className="add-package-btn" sx={{ borderRadius: '10px', background: 'linear-gradient(135deg, #3C0008 0%, #68000E 100%)' }}>
                                    {isEditMode ? 'Update Wallet Package' : 'Create Wallet Package'}
                                </Button>
                            </div>
                        </form>
                    </Box>
                </Modal>

                {/* 👁 VIEW CUSTOMER WALLET DETAILS MODAL */}
                <Modal open={openDetailsModal} onClose={() => setOpenDetailsModal(false)}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 520 },
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 3
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3C0008' }}>
                                👁 Customer Wallet Details
                            </Typography>
                            <IconButton size="small" onClick={() => setOpenDetailsModal(false)}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        {selectedWalletDetails && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px' }}>
                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
                                        👤 {selectedWalletDetails.customerId?.name || selectedWalletDetails.customerId?.customerName || 'Customer'}
                                    </div>
                                    <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
                                        📱 Mobile: {selectedWalletDetails.customerId?.phoneNumber || 'N/A'} {selectedWalletDetails.customerId?.email ? `| ✉️ ${selectedWalletDetails.customerId.email}` : ''}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Wallet Type:</span>
                                        <div style={{ fontWeight: 700, marginTop: '2px', color: '#1e293b' }}>
                                            <Chip
                                                label={`${selectedWalletDetails.type} Wallet`}
                                                color={selectedWalletDetails.type === 'Service' ? 'primary' : 'secondary'}
                                                size="small"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Wallet Plan Name:</span>
                                        <div style={{ fontWeight: 700, marginTop: '4px', color: '#0f172a' }}>
                                            {selectedWalletDetails.customWalletName || selectedWalletDetails.walletPackageId?.name || (selectedWalletDetails.type === 'Service' ? `${selectedWalletDetails.serviceId?.serviceName || 'Service'} Package` : 'Price Wallet')}
                                        </div>
                                    </div>
                                </div>

                                {selectedWalletDetails.type === 'Service' ? (
                                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '14px', borderRadius: '10px' }}>
                                        <div style={{ color: '#1e40af', fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>🏷️ Service Usage Breakdown</div>
                                        {selectedWalletDetails.services && selectedWalletDetails.services.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {selectedWalletDetails.services.map((s, idx) => (
                                                    <div key={s._id || idx} style={{ padding: '10px 12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                                        <div style={{ fontWeight: 700, color: '#1e3a8a', fontSize: '13.5px', marginBottom: '6px' }}>
                                                            • {s.serviceId?.serviceName || 'Service'}
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', fontSize: '12px', color: '#1e40af' }}>
                                                            <div>Base Usage: <strong>{s.baseCount || s.totalCount || 0}</strong></div>
                                                            <div>Additional Usage: <strong>{s.additionalCount || 0}</strong></div>
                                                            <div>Total Available: <strong>{s.totalCount || 0}</strong></div>
                                                            <div>Used: <strong>{s.usedCount || 0}</strong></div>
                                                            <div style={{ gridColumn: 'span 2', color: (s.remainingCount || 0) > 0 ? '#16a34a' : '#ef4444', fontWeight: 700, fontSize: '12.5px' }}>
                                                                Remaining: {s.remainingCount || 0} / {s.totalCount || 0}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#1e3a8a' }}>
                                                <div>Applicable Service: <strong>{selectedWalletDetails.serviceId?.serviceName || 'Service'}</strong></div>
                                                <div>Base Usages: <strong>{selectedWalletDetails.baseCount || selectedWalletDetails.totalCount || 0} Uses</strong></div>
                                                <div>Additional Usages: <strong>{selectedWalletDetails.additionalCount || 0} Free Uses</strong></div>
                                                <div>Total Allowed Usages: <strong>{selectedWalletDetails.totalCount || 0} Uses</strong></div>
                                                <div>Used Usages: <strong>{Math.max(0, (selectedWalletDetails.totalCount || 0) - (selectedWalletDetails.remainingCount || 0))} Uses</strong></div>
                                                <div>Remaining Usages: <strong style={{ color: '#16a34a', fontSize: '14px' }}>{selectedWalletDetails.remainingCount} / {selectedWalletDetails.totalCount}</strong></div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ background: '#fefce8', border: '1px solid #fef08a', padding: '14px', borderRadius: '10px' }}>
                                        <div style={{ color: '#854d0e', fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>💳 Cash Credit Breakdown</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#713f12' }}>
                                            <div>Total Credited Amount: <strong>₹{selectedWalletDetails.totalCredited || selectedWalletDetails.balance || 0}</strong></div>
                                            <div>Used Amount: <strong>₹{Number(Math.max(0, (selectedWalletDetails.totalCredited || 0) - (selectedWalletDetails.balance || 0))).toFixed(2)}</strong></div>
                                            <div>Current Balance: <strong style={{ color: '#16a34a', fontSize: '15px' }}>₹{Number(selectedWalletDetails.balance || 0).toFixed(2)}</strong></div>
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Purchased Amount:</span>
                                        <div style={{ fontWeight: 700, marginTop: '2px', color: '#0f172a' }}>₹{getWalletPurchasedAmount(selectedWalletDetails)}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Validity Period:</span>
                                        <div style={{ fontWeight: 700, marginTop: '2px' }}>{selectedWalletDetails.walletPackageId?.validityDays || 365} Days</div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Expiry Date:</span>
                                        <div style={{ fontWeight: 700, marginTop: '2px', color: '#475569' }}>
                                            {selectedWalletDetails.expiryDate ? new Date(selectedWalletDetails.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px dashed #cbd5e1' }}>
                                    <span style={{ color: '#64748b', fontSize: '12px' }}>Current Wallet Status:</span>
                                    <Chip
                                        label={new Date() > new Date(selectedWalletDetails.expiryDate) ? 'EXPIRED' : (selectedWalletDetails.status || 'ACTIVE')}
                                        color={new Date() > new Date(selectedWalletDetails.expiryDate) || selectedWalletDetails.status === 'EXPIRED' ? 'error' : 'success'}
                                        size="small"
                                        sx={{ fontWeight: 700 }}
                                    />
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <Button
                                variant="outlined"
                                startIcon={<Icon icon="mdi:printer" />}
                                onClick={() => handlePrintWallet(selectedWalletDetails)}
                                sx={{ borderRadius: '8px', color: '#3C0008', borderColor: '#3C0008' }}
                            >
                                Print Summary
                            </Button>
                            <Button variant="contained" onClick={() => setOpenDetailsModal(false)} sx={{ background: '#3C0008', borderRadius: '8px' }}>
                                Close
                            </Button>
                        </div>
                    </Box>
                </Modal>

                {/* 📜 SINGLE CUSTOMER WALLET HISTORY MODAL */}
                <Modal open={openSingleHistoryModal} onClose={() => setOpenSingleHistoryModal(false)}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '95%', sm: 850 },
                        maxHeight: '90vh',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{ padding: '16px 20px', background: '#3C0008', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '16px' }}>
                                <Icon icon="solar:history-bold-duotone" width="22" height="22" />
                                <span>Customer Wallet History</span>
                            </div>
                            <IconButton size="small" onClick={() => setOpenSingleHistoryModal(false)} sx={{ color: '#fff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                                Customer: <strong>{selectedWalletDetails?.customerId?.name || selectedWalletDetails?.customerId?.customerName || 'Selected Customer'}</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                Mobile: {selectedWalletDetails?.customerId?.phoneNumber || 'N/A'}
                            </div>
                        </div>

                        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                            {singleHistoryLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                    ⏳ Loading wallet history...
                                </div>
                            ) : singleCustomerHistory.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                    No wallet transactions found for this customer.
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                        <thead>
                                            <tr style={{ background: '#f1f5f9', color: '#334155', textAlign: 'left' }}>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Date & Time</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Type</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Action</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Wallet / Service</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Purchase Amount</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Usage / Amount</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Remaining Balance</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Bill Ref</th>
                                                <th style={{ padding: '10px 12px', borderBottom: '1px solid #cbd5e1' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {singleCustomerHistory.map((item, idx) => {
                                                const isPurchaseOrRecharge = item.transactionType === 'Purchase' || item.transactionType === 'Recharge';
                                                const purchaseAmtVal = isPurchaseOrRecharge
                                                    ? Number(item.amountPaid ?? item.totalAmount ?? item.price ?? item.walletId?.amountPaid ?? item.walletId?.walletPackageId?.price ?? 0)
                                                    : 0;
                                                return (
                                                    <tr key={item._id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#475569' }}>
                                                            {item.transactionDate ? new Date(item.transactionDate).toLocaleString('en-GB') : '-'}
                                                        </td>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <Chip label={item.walletType} size="small" variant="outlined" sx={{ fontSize: '10px', height: '18px' }} />
                                                        </td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{item.transactionType}</td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{item.walletName}</td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>
                                                            {purchaseAmtVal > 0 ? `₹${purchaseAmtVal.toFixed(2)}` : '-'}
                                                        </td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 700 }}>
                                                            {item.walletType === 'Service' ? `${item.serviceUsageCount || 1} Usage` : `₹${item.walletAmountUsed || item.walletCredit || 0}`}
                                                        </td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#16a34a' }}>
                                                            {item.walletType === 'Service' ? `${item.remainingServiceCount} Uses` : `₹${item.remainingWalletBalance}`}
                                                        </td>
                                                        <td style={{ padding: '10px 12px', color: '#64748b' }}>{item.posBillNumber || '-'}</td>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <Chip label={item.status || 'SUCCESS'} color="success" size="small" sx={{ fontSize: '10px', height: '18px' }} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </Box>
                </Modal>
            </div>
        </div>
    );
};

export default WalletManagementPage;
